import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CurrencySwitcher from '../../components/CurrencySwitcher';
import { useAdminAuth } from '../../context/AdminAuthContext';

const Topbar = ({ onSearch, onToggleSidebar, isCollapsed, onOpenMobile }) => {
  const navigate = useNavigate();
  const { user, isSuperAdmin, logout } = useAdminAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const displayName = user?.fullName || user?.name || user?.email || 'Admin';
  const roleLabel = isSuperAdmin
    ? 'Master Admin'
    : user?.primaryRole?.roleName || user?.primaryRole?.roleCode?.replaceAll('_', ' ') || user?.adminType?.replaceAll('_', ' ') || 'Sub Admin';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'AD';

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout?.();
    } finally {
      navigate('/admin/login', { replace: true });
      setLoggingOut(false);
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-[color:var(--admin-border)] bg-white/92 px-4 py-4 backdrop-blur-xl md:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            type="button"
            onClick={onOpenMobile}
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] text-[color:var(--admin-heading)] shadow-sm transition hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)] lg:hidden"
            aria-label="Open menu"
          >
            <i className="bi bi-list text-lg" />
          </button>
          <button
            type="button"
            onClick={onToggleSidebar}
            className="hidden h-11 w-11 items-center justify-center rounded-2xl border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] text-[color:var(--admin-heading)] shadow-sm transition hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)] lg:flex"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`bi ${isCollapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'} text-base`} />
          </button>
          <div className="relative min-w-0 flex-1 max-w-2xl">
            <i className="bi bi-search pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[color:var(--admin-icon)]" />
            <input
              type="text"
              placeholder="Search properties, bookings, customers, hosts, reports..."
              onChange={(e) => onSearch?.(e.target.value)}
              className="w-full rounded-[18px] border border-[color:var(--admin-border)] bg-[var(--admin-bg)] py-3 pl-11 pr-20 text-sm text-[color:var(--admin-heading)] outline-none shadow-sm transition placeholder:text-[color:var(--admin-icon)] focus:border-[var(--admin-accent)] focus:bg-white focus:ring-4 focus:ring-[rgba(15,92,90,0.08)]"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-[color:var(--admin-border)] bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--admin-icon)]">
              Ctrl+/
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="hidden items-center gap-2 xl:flex">
            {[
              { label: 'New Property', tone: 'primary', to: '/admin/properties/new' },
              { label: 'New Package', tone: 'default', to: '/admin/packages' },
              { label: 'New Booking', tone: 'default', to: '/admin/bookings' },
            ].map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => navigate(action.to)}
                className={`whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  action.tone === 'primary'
                    ? 'bg-[var(--admin-accent)] text-white shadow-[0_12px_24px_rgba(15,92,90,0.18)] hover:bg-[#0d504e]'
                    : 'border border-[color:var(--admin-border)] bg-white text-[color:var(--admin-heading)] shadow-sm hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)]'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 rounded-full border border-[color:var(--admin-border)] bg-white px-3 py-2 shadow-sm">
            <CurrencySwitcher
              compact
              labelClassName="text-[color:var(--admin-text)]"
              selectClassName="rounded-full border border-[color:var(--admin-border)] bg-[var(--admin-bg)] px-2 py-1 text-xs text-[color:var(--admin-heading)] outline-none focus:border-[var(--admin-accent)]"
            />
            <button className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] text-[color:var(--admin-text)] transition hover:text-[var(--admin-accent)]">
              <i className="bi bi-bell text-base" />
              <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-[var(--admin-highlight)] ring-2 ring-white" />
            </button>
            <div className="flex items-center gap-3 border-l border-[color:var(--admin-border)] pl-3">
              <div className="text-right leading-tight">
                <p className="text-sm font-semibold text-[color:var(--admin-heading)]">{displayName}</p>
                <p className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--admin-text)]">{roleLabel}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--admin-accent)] text-sm font-bold text-white shadow-[0_10px_20px_rgba(15,92,90,0.2)]">
                {initials}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                className="rounded-full border border-[color:var(--admin-border)] bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--admin-heading)] transition hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
